// packages/web/src/lib/coverFrame.ts — P2: THE COVER FRAME, EXTRACTED
// CLIENT-SIDE.
//
// Above the fold on a phone, before any tap, a hero reel is a STILL IMAGE.
// Left alone that still is frame 0 — for most demo reels a black slate or a
// title card — so the artist scrubs their own video and picks a better one.
//
// ⚠ NO SERVER PIPELINE. No ffmpeg, no sharp, nothing that touches the byte
// stream server-side (`mediaDelivery.ts` answers 302 -> a presigned R2 URL;
// the upload is the ONLY place bytes are ever decoded, and that happens here,
// in the artist's own browser, on their own device). The whole mechanism is a
// detached `<video>` element + `canvas.drawImage`.
//
// ⚠ NEVER SILENT. An unsupported codec, a browser that will not decode, a
// seek that never resolves — every one of those throws a
// `CoverFrameExtractionError` with a plain sentence, never a swallowed
// rejection. The caller's job is to say so and let the artist attach a still
// themselves (the `cover_frame_ref` field takes a plain image upload same as
// any other file field — see RecordForm.tsx's FileField).

/** Loud, named failure — see the module header. `cause` carries whatever the
 * browser actually said (a MediaError code, a DOMException name, …) for a
 * console reader; `message` is written for the person on the screen. */
export class CoverFrameExtractionError extends Error {
  constructor(
    message: string,
    readonly cause2?: unknown
  ) {
    super(message);
    this.name = 'CoverFrameExtractionError';
  }
}

/** Not frame 0 — a couple of seconds in is almost always past the slate. For
 * a very short clip that clamps to the midpoint so the seek target is never
 * past the end of the video. */
export function defaultSeekSeconds(durationSeconds: number): number {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return 0;
  const target = 2;
  return Math.min(target, durationSeconds / 2);
}

const LOAD_TIMEOUT_MS = 15_000;
const SEEK_TIMEOUT_MS = 10_000;
/** The still is a JPEG for a hero slot, not a source asset — this ceiling
 * keeps a 4K reel's cover frame from outweighing the page it sits on. */
const MAX_FRAME_WIDTH_PX = 1280;
const JPEG_QUALITY = 0.85;

function withTimeout<T>(promise: Promise<T>, ms: number, onTimeoutMessage: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new CoverFrameExtractionError(onTimeoutMessage)), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

/** A detached `<video>` bound to `file` — never inserted into the document,
 * so nothing about extraction depends on layout (and nothing it does is
 * visible until the caller chooses to show a preview). Caller MUST call the
 * returned `revoke()` when done, on every path including failure. */
async function loadDetachedVideo(file: File): Promise<{ video: HTMLVideoElement; revoke: () => void }> {
  if (typeof document === 'undefined' || typeof document.createElement !== 'function') {
    throw new CoverFrameExtractionError('This browser has no video element to extract a frame with.');
  }
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  // ⚠ NOT preload="none" here — this is a LOCAL blob already fully in the
  // artist's own device memory, not a network fetch, so "preload" costs
  // nothing. The `preload="none"` rule (P0/F0) is about bytes already saved
  // to R2 that a STRANGER's phone would otherwise fetch — a different moment
  // entirely from the artist choosing their own frame at upload time.
  video.preload = 'auto';
  video.src = url;

  const revoke = () => URL.revokeObjectURL(url);

  const ready = new Promise<void>((resolve, reject) => {
    const onLoaded = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      const code = video.error?.code;
      const reason =
        code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED
          ? 'This browser cannot decode this video format.'
          : code === MediaError.MEDIA_ERR_DECODE
            ? 'This video could not be decoded — it may be corrupt or use an unsupported codec.'
            : 'This video could not be read.';
      reject(new CoverFrameExtractionError(reason, video.error));
    };
    const cleanup = () => {
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('error', onError);
    };
    video.addEventListener('loadedmetadata', onLoaded, { once: true });
    video.addEventListener('error', onError, { once: true });
  });

  try {
    await withTimeout(ready, LOAD_TIMEOUT_MS, 'This video took too long to open — try a different file.');
  } catch (err) {
    revoke();
    throw err;
  }
  return { video, revoke };
}

/** Seeks `video` to `seconds` (clamped to the clip's own duration) and
 * resolves once the frame at that time is actually decoded and ready to
 * draw — never before the `seeked` event, which is what makes the
 * subsequent `drawImage` deterministic rather than a race with decode. */
async function seekTo(video: HTMLVideoElement, seconds: number): Promise<void> {
  const duration = Number.isFinite(video.duration) ? video.duration : 0;
  const clamped = Math.max(0, Math.min(seconds, Math.max(0, duration - 0.05)));
  const seeked = new Promise<void>((resolve, reject) => {
    const onSeeked = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new CoverFrameExtractionError('This video could not be seeked to that moment.', video.error));
    };
    const cleanup = () => {
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
    };
    video.addEventListener('seeked', onSeeked, { once: true });
    video.addEventListener('error', onError, { once: true });
  });
  video.currentTime = clamped;
  await withTimeout(seeked, SEEK_TIMEOUT_MS, 'Choosing that moment took too long — try scrubbing to a different spot.');
}

/** Draws the video's CURRENT frame to a canvas and exports it as a JPEG
 * blob, scaled down to `MAX_FRAME_WIDTH_PX` wide. A tainted canvas
 * (cross-origin source — should never happen for a local blob, but the
 * browser's own security model can still refuse) surfaces as a loud,
 * named error rather than a silent empty blob. */
async function captureFrame(video: HTMLVideoElement): Promise<Blob> {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) {
    throw new CoverFrameExtractionError('This video reported no picture size — nothing to capture.');
  }
  const scale = Math.min(1, MAX_FRAME_WIDTH_PX / vw);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(vw * scale);
  canvas.height = Math.round(vh * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new CoverFrameExtractionError('This browser could not draw the frame.');
  try {
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  } catch (err) {
    throw new CoverFrameExtractionError('This frame could not be captured.', err);
  }
  const blob = await new Promise<Blob | null>((resolve, reject) => {
    try {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY);
    } catch (err) {
      // A tainted canvas throws SecurityError from toBlob/toDataURL — the
      // one failure mode `drawImage` itself does not raise.
      reject(new CoverFrameExtractionError('This frame could not be exported — the browser refused it.', err));
    }
  });
  if (!blob) throw new CoverFrameExtractionError('This frame could not be exported.');
  return blob;
}

export interface ExtractedCoverFrame {
  blob: Blob;
  atSeconds: number;
  durationSeconds: number;
}

/**
 * The whole act: open `file` in a detached video, seek to `atSeconds` (or a
 * sensible default — never frame 0), capture the frame, and hand back the
 * JPEG bytes plus where it was taken. Always cleans up its object URL, on
 * every path including a thrown error.
 *
 * Never swallows a failure — every rejection is a `CoverFrameExtractionError`
 * with a plain sentence the caller can show as-is.
 */
export async function extractCoverFrame(file: File, atSeconds?: number): Promise<ExtractedCoverFrame> {
  const { video, revoke } = await loadDetachedVideo(file);
  try {
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const target = atSeconds ?? defaultSeekSeconds(duration);
    await seekTo(video, target);
    const blob = await captureFrame(video);
    return { blob, atSeconds: Math.max(0, Math.min(target, duration)), durationSeconds: duration };
  } finally {
    revoke();
  }
}

/** `cover-<original name>.jpg`, so a downloaded/inspected still is legible
 * beside the reel it came from rather than a bare "blob.jpg". */
export function coverFrameFilename(originalFilename: string): string {
  const stem = originalFilename.replace(/\.[^./\\]+$/, '').trim();
  return `cover-${stem || 'frame'}.jpg`;
}
